import React, { useEffect, useState } from 'react';

import api from '../../api/axios';

const specializationColor = (code) => {
  const map = {
    CARD_ISSUE: 'bg-blue-100 text-blue-700',
    APPROVAL_ISSUE: 'bg-purple-100 text-purple-700',
    REIMBURSEMENT_ISSUE: 'bg-green-100 text-green-700',
    TECHNICAL_ISSUE: 'bg-amber-100 text-amber-700',
    ONBOARDING_ISSUE: 'bg-teal-100 text-teal-700',
    REPORT_ISSUE: 'bg-indigo-100 text-indigo-700',
    OTHER: 'bg-gray-100 text-gray-600',
  };
  return map[code] || 'bg-gray-100 text-gray-600';
};

const ManageSpecializations = () => {
  const [tab, setTab] = useState('types'); // 'types' | 'agents'
  const [specializations, setSpecializations] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newCode, setNewCode] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const [editingAgentId, setEditingAgentId] = useState(null);
  const [editingSpecs, setEditingSpecs] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      api.get('/api/admin/specializations'),
      api.get('/api/admin/agents'),
    ]).then(([specRes, agentRes]) => {
      setSpecializations(specRes.data);
      setAgents(agentRes.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const activeSpecs = specializations.filter(s => s.active);
  const inactiveSpecs = specializations.filter(s => !s.active);

  const handleAdd = async () => {
    setError('');
    if (!newCode.trim() || !newLabel.trim()) { setError('Both code and label are required.'); return; }
    setAdding(true);
    try {
      await api.post('/api/admin/specializations', {
        code: newCode.trim().toUpperCase().replace(/\s+/g, '_'),
        label: newLabel.trim(),
      });
      setNewCode(''); setNewLabel('');
      fetchAll();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to add specialization.');
    } finally { setAdding(false); }
  };

  const handleToggle = async (id) => {
    await api.post(`/api/admin/specializations/${id}/toggle`);
    fetchAll();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this specialization?')) return;
    await api.delete(`/api/admin/specializations/${id}`);
    fetchAll();
  };

  const startEdit = (agent) => {
    setEditingAgentId(agent.agentId);
    setEditingSpecs([...(agent.specializations || [])]);
  };

  const cancelEdit = () => { setEditingAgentId(null); setEditingSpecs([]); };

  const toggleSpec = (code) => {
    setEditingSpecs(prev =>
      prev.includes(code) ? prev.filter(s => s !== code) : [...prev, code]
    );
  };

  const saveAgentSpecs = async (agentId) => {
    setSaving(true);
    try {
      await api.post(`/api/admin/agents/${agentId}/specializations`, editingSpecs);
      setAgents(prev => prev.map(a =>
        a.agentId === agentId ? { ...a, specializations: editingSpecs } : a
      ));
      cancelEdit();
    } catch (e) {
      console.error(e);
    } finally { setSaving(false); }
  };

  const tabs = [
    { key: 'types', label: 'Specialization Types', count: activeSpecs.length },
    { key: 'agents', label: 'Agent Assignments', count: agents.length },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="w-full px-4 py-8">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Manage Specializations</h1>
          <p className="text-gray-500 text-sm">Define specialization types and assign them to support agents</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === t.key
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                tab === t.key ? 'bg-primary/10 text-primary' : 'bg-gray-200 text-gray-500'
              }`}>
                {t.count}
              </span>
            </button>
          ))}
        </div>

        {/* ── TAB 1: Specialization Types ── */}
        {tab === 'types' && (
          <div className="space-y-4">
            {/* Add new */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-4">Add New Specialization Type</h2>
              <div className="flex gap-3 mb-3">
                <div className="flex-1">
                  <label className="block text-xs text-gray-400 mb-1">Label <span className="text-gray-300">(e.g. Payment Issue)</span></label>
                  <input
                    type="text"
                    value={newLabel}
                    onChange={e => { setNewLabel(e.target.value); setNewCode(e.target.value.trim().toUpperCase().replace(/\s+/g, '_')); }}
                    placeholder="Payment Issue"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-400 mb-1">Code <span className="text-gray-300">(auto-generated)</span></label>
                  <input
                    type="text"
                    value={newCode}
                    onChange={e => setNewCode(e.target.value.toUpperCase().replace(/\s+/g, '_'))}
                    placeholder="PAYMENT_ISSUE"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              </div>
              {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
              <button onClick={handleAdd} disabled={adding}
                className="bg-primary text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-primary-light disabled:opacity-50 transition-colors">
                {adding ? 'Adding...' : '+ Add Specialization'}
              </button>
            </div>

            {/* Active types */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-700">Active Types</h2>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{activeSpecs.length} active</span>
              </div>
              {loading ? (
                <div className="text-center py-8 text-gray-400 text-sm">Loading...</div>
              ) : activeSpecs.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">No active specializations.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-400 uppercase bg-gray-50">
                      <th className="text-left px-5 py-3 font-medium">Label</th>
                      <th className="text-left px-5 py-3 font-medium">Code</th>
                      <th className="text-right px-5 py-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {activeSpecs.map(s => (
                      <tr key={s.id} className="hover:bg-gray-50">
                        <td className="px-5 py-3 font-medium text-gray-800">{s.label}</td>
                        <td className="px-5 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full font-mono ${specializationColor(s.code)}`}>{s.code}</span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => handleToggle(s.id)}
                              className="text-xs text-amber-600 border border-amber-200 px-3 py-1 rounded-lg hover:bg-amber-50 transition-colors">
                              Deactivate
                            </button>
                            <button onClick={() => handleDelete(s.id)}
                              className="text-xs text-red-500 border border-red-200 px-3 py-1 rounded-lg hover:bg-red-50 transition-colors">
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Inactive types */}
            {inactiveSpecs.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-500">Inactive Types</h2>
                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{inactiveSpecs.length}</span>
                </div>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-gray-50">
                    {inactiveSpecs.map(s => (
                      <tr key={s.id} className="hover:bg-gray-50 opacity-60">
                        <td className="px-5 py-3 font-medium text-gray-500">{s.label}</td>
                        <td className="px-5 py-3 font-mono text-xs text-gray-400">{s.code}</td>
                        <td className="px-5 py-3 text-right">
                          <button onClick={() => handleToggle(s.id)}
                            className="text-xs text-green-600 border border-green-200 px-3 py-1 rounded-lg hover:bg-green-50 transition-colors">
                            Activate
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── TAB 2: Agent Assignments ── */}
        {tab === 'agents' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700">Agent Specialization Assignments</h2>
              <p className="text-xs text-gray-400 mt-0.5">Click Edit on any agent to update their specializations</p>
            </div>
            {loading ? (
              <div className="text-center py-12 text-gray-400 text-sm">Loading...</div>
            ) : agents.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">No agents found.</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {agents.map(agent => {
                  const isEditing = editingAgentId === agent.agentId;
                  return (
                    <div key={agent.agentId} className={`px-5 py-4 transition-colors ${isEditing ? 'bg-blue-50/40' : 'hover:bg-gray-50'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{agent.name}</p>
                          <p className="text-xs text-gray-400">{agent.agentId} &middot; {agent.email}</p>
                        </div>
                        {!isEditing ? (
                          <button onClick={() => startEdit(agent)}
                            className="text-xs text-primary border border-primary/20 px-3 py-1 rounded-lg hover:bg-primary/5 transition-colors font-medium">
                            Edit
                          </button>
                        ) : (
                          <div className="flex gap-2">
                            <button onClick={() => saveAgentSpecs(agent.agentId)} disabled={saving}
                              className="text-xs bg-primary text-white px-3 py-1 rounded-lg hover:bg-primary-light disabled:opacity-50 transition-colors font-medium">
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button onClick={cancelEdit}
                              className="text-xs border border-gray-200 text-gray-500 px-3 py-1 rounded-lg hover:bg-gray-100 transition-colors">
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>

                      {/* View mode */}
                      {!isEditing && (
                        <div className="flex flex-wrap gap-1">
                          {(agent.specializations || []).length > 0 ? (
                            agent.specializations.map(code => (
                              <span key={code} className={`text-xs font-medium px-2 py-0.5 rounded-full ${specializationColor(code)}`}>
                                {specializations.find(s => s.code === code)?.label || code}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400 italic">No specializations assigned</span>
                          )}
                        </div>
                      )}

                      {/* Edit mode */}
                      {isEditing && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {activeSpecs.map(spec => {
                            const checked = editingSpecs.includes(spec.code);
                            return (
                              <label key={spec.code}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                                  checked ? 'border-primary bg-primary/5' : 'border-gray-200 bg-white hover:bg-gray-50'
                                }`}>
                                <input type="checkbox" checked={checked} onChange={() => toggleSpec(spec.code)}
                                  className="accent-primary w-3.5 h-3.5" />
                                <span className={`text-xs font-medium ${checked ? 'text-primary' : 'text-gray-700'}`}>
                                  {spec.label}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default ManageSpecializations;
